import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Link } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { IAlertEvent } from "@/domain/alertEvent.interface";
import { EVENT_ENUM } from "../../domain/events.enum";
import { BeneficiaryItem } from "@/domain/beneficiary.interface";
import { useAppTheme } from "../../utils/theme";
import { useUserStore } from "../../stores/user.store";
import { useBeneficiaryTerm } from "../../hooks/useBeneficiaryTerm";

export const createLinkedNarrative = (
	alertEvent: IAlertEvent,
): React.ReactNode => {
	const theme = useAppTheme();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { userHasAccessToCode } = useUserStore();
	const { singular } = useBeneficiaryTerm();
	const showCIN = userHasAccessToCode("DASH_EVT_BEN_CIN_VIEW");
	const {
		actionCode,
		beneficiaryId,
		fullName,
		program,
		narrative,
		beneficiary,
	} = alertEvent;
	const narrativeName =
		`${beneficiary?.firstName} ${beneficiary?.lastName}`.trim();
	const narrativeCIN = `${beneficiary?.sims?.clientIdNumber}`.trim();
	const beneficiaryIdString =
		typeof beneficiaryId === "string"
			? beneficiaryId
			: (beneficiaryId as BeneficiaryItem)?._id;
	const linkedName = (
		<Link
			component={RouterLink}
			to={`/beneficiaries/${beneficiaryIdString}`}
			underline="hover"
			style={{
				textDecoration: "underline",
				color: theme.palette.textMain.dark4,
				cursor: "pointer",
			}}
			onClick={(e) => {
				e.preventDefault();
				queryClient.invalidateQueries();
				navigate(`/beneficiaries/${beneficiaryIdString}`);
			}}
		>
			{showCIN ? narrativeCIN : narrativeName || fullName}
		</Link>
	);

	let backendNarrative = narrative;
	if (narrativeCIN && narrative.includes(narrativeCIN)) {
		backendNarrative = narrative.split(narrativeCIN)[1];
	} else if (narrativeName && narrative.includes(narrativeName)) {
		backendNarrative = narrative.split(narrativeName)[1];
	}

	switch (actionCode) {
		case EVENT_ENUM.EVE_BEN_ENROLL:
			return (
				<>
					{singular} {linkedName} has been enrolled to the {program?.name}{" "}
					program!
				</>
			);

		case EVENT_ENUM.EVE_BEN_COMPL:
			return (
				<>
					{singular} {linkedName} has completed the {program?.name} program!
				</>
			);

		case EVENT_ENUM.EVE_BEN_DISENROLL:
			return (
				<>
					{singular} {linkedName} has been disenrolled from the {program?.name}{" "}
					program!
				</>
			);

		case EVENT_ENUM.EVE_MOVE_TO:
			return (
				<>
					{singular} {linkedName}
					{backendNarrative}
				</>
			);

		case EVENT_ENUM.EVE_MOVE_FROM:
			return backendNarrative ? (
				<>
					{singular} {linkedName}
					{backendNarrative}
				</>
			) : (
				<>{narrative}</>
			);

		case EVENT_ENUM.EVE_USER_ENROLL:
			return <>{narrative}</>;

		case EVENT_ENUM.EVE_PROV_ACCOUNT:
			return <>{narrative}</>;

		case EVENT_ENUM.EVE_MISSED_ENTRY:
			return (
				<>
					{singular} {linkedName} has missed entries.
				</>
			);

		case EVENT_ENUM.EVE_CM_ELIG:
			return (
				<>
					{singular} {linkedName} requires a CM eligibility reconfirmation.
				</>
			);

		case EVENT_ENUM.EVE_31DAYS_ABS:
			return backendNarrative ? (
				<>
					{linkedName}
					{backendNarrative}
				</>
			) : (
				<>
					{singular} {linkedName} should be disenrolled from the {program?.name}{" "}
					program due to 31 days of absence.
				</>
			);

		case EVENT_ENUM.EVE_UDT_NOTES_UPD:
		case EVENT_ENUM.EVE_UDT_RESULT_UPD: {
			const identifier =
				narrativeCIN && narrative.includes(narrativeCIN)
					? narrativeCIN
					: narrativeName && narrative.includes(narrativeName)
						? narrativeName
						: null;
			if (identifier) {
				const [before, after] = narrative.split(identifier);
				return (
					<>
						{before}
						{linkedName}
						{after}
					</>
				);
			}
			return <>{narrative}</>;
		}

		default:
			return alertEvent.narrative;
	}
};
